<template>
  <q-page class="q-mb-xl">
    <!-- <div class="text-h6 text-dark text-weight-bold text-center">
      {{ t("your_cart") }}
    </div>
    <separator size="3px" color="grey-4" /> -->
    <div class="text-body2 text-dark text-weight-bold text-center q-pa-sm">
      <div>
        <div class="row justify-between">
          <div class="">{{ formatCurrency(0) }}</div>
          <template v-if="progress >= 1">
            {{ t("free_shipping_success") }}
          </template>
          <template v-else>
            {{
              t("free_shipping_progress", {
                amount: formatCurrency(remainingAmount)
              })
            }}
          </template>
          <div class="text-positive">
            {{ formatCurrency(minOrder) }}
          </div>
        </div>
        <q-linear-progress
          size="20px"
          :value="progress"
          color="positive"
          class="q-mt-xs"
        />
      </div>
    </div>
    <separator size="3px" color="grey-4" />
    <div class="q-px-md">
      <product-cart
        v-if="cartProducts?.items"
        :items="cartProducts.items"
        @remove-item="handleRemoveItem"
        @clear-cart="clearCartItem"
        @update-quantity="handleUpdateQuantity"
      ></product-cart>
    </div>
    <div
      class="bg-secondary text-white row items-center justify-between q-px-sm full-width fixed-bottom"
      style="margin-bottom: 82px"
    >
      <div class="text-subtitle1">
        {{ t("sub_total") }} :
        <span class="text-weight-bold">{{
          formatCurrency(cartProducts?.cart?.total_price || 0)
        }}</span>
      </div>
      <q-separator vertical size="1px" color="grey-4" class="q-ml-sm" />
      <q-btn
        :disable="cartProducts?.items.docs.length === 0"
        unelevated
        label="CHECK OUT"
        class="text-weight-bold q-px-none"
        :icon-right="mdiCart"
        @click="
          router.push({ name: NAIL_SUPPLY_ROUTE_NAME.SHIPPING_INFORMATION })
        "
      />
    </div>
  </q-page>
</template>

<script setup lang="ts">
import Separator from "@/components/Separator.vue";
import ProductCart from "@/components/card/ProductCart.vue";
import { useBase } from "@/composables/useBase";
import { usePromotionCode } from "@/composables/usePromotionCode";
import { useCart } from "@/composables/useCart";
import { useLang } from "@/composables/useLang";
import { useCartStore } from "@/stores/cartStore";

import { formatCurrency } from "@/utils/currencyUtil";
import { computed, onMounted } from "vue";
import { useSEOMeta } from "@/composables/useSEOMeta";
import { useAppStore } from "@/stores/appStore";
import { NAIL_SUPPLY_ROUTE_NAME } from "@/libs/route-name";
import { mdiCart } from "@quasar/extras/mdi-v7";

const { t } = useLang();
const { router } = useBase();
const { autoCachePromotionCode } = usePromotionCode();
const { userCart, removeProductInCart, clearCart } = useCart();
const { updateQuantityItem } = useCart();
const { appLoadingCart, appLoading } = useBase();
const cartStore = useCartStore();
const appStore = useAppStore();

onMounted(async () => {
  appLoading(true);
  autoCachePromotionCode();
  await userCart();
  appLoading(false);
});

const cartProducts = computed(() => {
  return cartStore.getCurrentCart;
});

const progress = computed(() => {
  const subtotal = cartProducts?.value?.cart?.total_price || 0;
  const percent =
    subtotal / appStore?.feeConfiguration?.minOrderForFreeShipping;
  return Math.min(Math.max(percent, 0), 1);
});

const minOrder = computed(() => {
  return appStore?.feeConfiguration?.minOrderForFreeShipping || 0;
});

const remainingAmount = computed(() => {
  const minOrder = appStore?.feeConfiguration?.minOrderForFreeShipping || 0;
  const totalPrice = cartProducts?.value?.cart?.total_price || 0;
  return Math.max(minOrder - totalPrice, 0);
});

const handleRemoveItem = async (id: string) => {
  appLoadingCart(true);
  await removeProductInCart(id);
  appLoadingCart(false);
};

const clearCartItem = async () => {
  appLoadingCart(true);
  await clearCart();
  appLoadingCart(false);
};

const handleUpdateQuantity = async ({
  id,
  quantity
}: {
  id: string;
  quantity: number;
}) => {
  appLoadingCart(true);
  await updateQuantityItem(id, quantity);
  appLoadingCart(false);
};

useSEOMeta({
  title: "Shopping Cart",
  description:
    "Review your nail supply orders and proceed to checkout. Manage your cart items and complete your purchase.",
  keywords:
    "Nailsupply cart, Order checkout, Nail supplies order, Shopping cart",
  url: "/order",
  noIndex: true,
  twitterHandle: "@nailsupply"
});
</script>

<style scoped></style>
